const STORAGE_PREFIX = 'oi.order-idempotency.v1';
const processMemoryRecords = new Map();

function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((result, key) => {
      const normalized = canonicalize(value[key]);
      if (normalized !== undefined) result[key] = normalized;
      return result;
    }, {});
  }
  return value;
}

export function canonicalOrderPayload(payload) {
  return JSON.stringify(canonicalize(payload));
}

export function randomIdempotencyKey() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function storageKey(routeKind, actorId) {
  return `${STORAGE_PREFIX}:${routeKind}:${actorId}`;
}

export function getOrderIdempotencyKey({ routeKind, actorId, payload, storage = sessionStorage }) {
  if (!routeKind || !actorId) throw new Error('Order idempotency identity is required');
  const payloadCanonical = canonicalOrderPayload(payload);
  const key = storageKey(routeKind, actorId);
  let stored = null;
  try { stored = JSON.parse(storage.getItem(key) || 'null'); } catch { /* use process memory */ }
  const inMemory = processMemoryRecords.get(key) || null;
  for (const existing of [inMemory, stored]) {
    if (existing?.payloadCanonical === payloadCanonical && typeof existing?.idempotencyKey === 'string') {
      processMemoryRecords.set(key, existing);
      return existing.idempotencyKey;
    }
  }

  const idempotencyKey = randomIdempotencyKey();
  const record = { idempotencyKey, payloadCanonical };
  processMemoryRecords.set(key, record);
  try {
    storage.setItem(key, JSON.stringify(record));
  } catch { /* process memory protects retries for this app session */ }
  return idempotencyKey;
}

export function clearOrderIdempotencyKey({
  routeKind,
  actorId,
  storage = sessionStorage,
  idempotencyKey,
}) {
  if (!routeKind || !actorId) return;
  const key = storageKey(routeKind, actorId);
  if (idempotencyKey) {
    let stored = null;
    try { stored = JSON.parse(storage.getItem(key) || 'null'); } catch { /* use process memory */ }
    const inMemory = processMemoryRecords.get(key) || null;
    const current = inMemory || stored;
    if (current?.idempotencyKey !== idempotencyKey) return;
  }
  processMemoryRecords.delete(key);
  try { storage.removeItem(key); } catch { /* process memory was still cleared */ }
}

function validatedCreatedOrder(data) {
  const order = data?.order;
  if (
    !order
    || typeof order !== 'object'
    || typeof order.id !== 'string'
    || !order.id.trim()
    || typeof order.order_number !== 'string'
    || !order.order_number.trim()
    || !Array.isArray(order.items)
    || typeof order.total !== 'number'
    || !Number.isFinite(order.total)
    || order.total < 0
  ) {
    throw new Error('Order creation response could not be verified');
  }
  return order;
}

function validateOrderAgainstSubmission(order, { customerId, payload }) {
  if (
    typeof customerId !== 'string'
    || !customerId
    || !payload
    || typeof payload !== 'object'
    || !Array.isArray(payload.items)
    || !payload.shipping_address
    || typeof payload.shipping_address !== 'object'
    || !order.shipping_address
    || typeof order.shipping_address !== 'object'
    || order.user_id !== customerId
    || order.payment_method !== payload.payment_method
  ) {
    throw new Error('Order creation response did not match the submitted order');
  }
  const submittedItems = [...payload.items, ...(payload.custom_items || []).map(item=>({...item,product_id:`custom:${item.line_id}`}))].map(item => ({
    product_id: item?.product_id,
    quantity: item?.quantity,
  }));
  const responseItems = order.items.map(item => ({
    product_id: item?.product_id,
    quantity: item?.quantity,
  }));
  if (canonicalOrderPayload(responseItems) !== canonicalOrderPayload(submittedItems)) {
    throw new Error('Order creation response did not match the submitted order');
  }
  for (const expected of payload.custom_items || []) {
    const item=order.items.find(row=>row.product_id===`custom:${expected.line_id}`);
    if(!item || item.sku!==expected.sku || item.name!==expected.name || item.specification!==expected.specification || item.price!==expected.price || Math.abs(item.line_total-expected.price*expected.quantity)>.005) throw new Error('One-off order line did not match the reviewed product');
  }
  const bill=payload.billing_only || [];
  if(bill.length || order.billing_only?.length) {
    const fields=row=>({line_id:row.line_id,sku:row.sku,name:row.name,specification:row.specification || '',quantity:row.quantity,price:row.price});
    if(canonicalOrderPayload(bill.map(fields))!==canonicalOrderPayload((order.billing_only||[]).map(fields)))throw new Error('Prior billing lines did not match the reviewed invoice');
  }
  if (bill.length || payload.custom_items?.length) {
    const expectedTotal=order.items.reduce((s,x)=>s+Math.round(x.price*100)*x.quantity,0)+bill.reduce((s,x)=>s+Math.round(x.price*100)*x.quantity,0)+Math.round(order.shipping_cost*100);
    if(Math.round(order.total*100)!==expectedTotal)throw new Error('Invoice total did not match reviewed charges');
  }
  if (payload.shipping_override != null && order.shipping_cost !== payload.shipping_override) throw new Error('Order shipping did not match the approved allowance');
  for (const [productId, expected] of Object.entries(payload.price_overrides || {})) {
    const item = order.items.find(row => row.product_id === productId);
    if (!item || item.price !== expected || Math.abs(item.line_total - expected * item.quantity) > 0.005) {
      throw new Error('Order prices did not match the reviewed kit prices');
    }
  }
  for (const [field, expected] of Object.entries(payload.shipping_address)) {
    if (field === 'phone' && !expected) continue;
    if (order.shipping_address[field] !== expected) {
      throw new Error('Order creation response did not match the submitted order');
    }
  }
}

export function validateCustomerOrderCreationResponse(data, { actorId, payload }) {
  const order = validatedCreatedOrder(data);
  if (
    !data.payment_instructions
    || typeof data.payment_instructions !== 'object'
    || Array.isArray(data.payment_instructions)
    || Object.keys(data.payment_instructions).length === 0
  ) {
    throw new Error('Order payment instructions could not be verified');
  }
  validateOrderAgainstSubmission(order, { customerId: actorId, payload });
  return order;
}

export function validateAdminOrderCreationResponse(data, { customerId, payload }) {
  const order = validatedCreatedOrder(data);
  if (data.success !== true) {
    throw new Error('Admin order creation response could not be verified');
  }
  validateOrderAgainstSubmission(order, { customerId, payload });
  return order;
}
